import { useState } from "react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { Checkbox } from "@/ui/components/ui/checkbox";
import { useToast } from "@/controllers/hooks/use-toast";
import { Loader2, Crown } from "lucide-react";
import { supabase } from "@/infra/supabase/client";
import { irParaOsAgentes } from "@/config/agentes";
import { useNavigate } from "react-router-dom";
import { logger } from "@/utils/logger";

interface RegisterFormProps {
  prefilledEmail?: string;
}

export default function RegisterForm({ prefilledEmail }: RegisterFormProps) {
  const [formData, setFormData] = useState({
    email: prefilledEmail || "",
    password: "",
    confirmPassword: "",
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "🔐 Senhas Não Coincidem",
        description:
          "As senhas digitadas são diferentes. Verifique e tente novamente.",
        variant: "destructive",
        className: "bg-red-950/80 text-red-200 border-red-900",
      });
      return;
    }
    if (!acceptTerms) {
      toast({
        title: "📋 Aceitar Termos",
        description:
          "É obrigatório aceitar os termos de uso para continuar o cadastro.",
        variant: "destructive",
        className: "bg-red-950/80 text-red-200 border-red-900",
      });
      return;
    }
    setIsLoading(true);
    try {
      // 1. VALIDAR E RESERVAR PRÉ-CADASTRO ATOMICAMENTE
      // Marca como usado na mesma query para evitar race condition (TOCTOU)
      const { data: preRegs, error: claimError } = await supabase
        .from("pre_registrations")
        .update({
          is_used: true,
          used_at: new Date().toISOString(),
        })
        .eq("email", formData.email.toLowerCase())
        .eq("is_used", false)
        .select("*");

      if (claimError) {
        toast({
          title: "Erro de Verificação",
          description:
            "Não foi possível verificar a autorização do email. Tente novamente.",
          variant: "destructive",
          className: "bg-red-950/80 text-red-200 border-red-900",
        });
        return;
      }

      const preReg = preRegs?.[0];
      if (!preReg) {
        logger.warn("auth", "Registro negado - sem pré-cadastro", {
          email: formData.email.toLowerCase(),
        });
        toast({
          title: "Acesso Negado",
          description:
            "Por enquanto, o acesso está restrito a pessoas com link de convite.",
          variant: "destructive",
        });
        return;
      }

      // 2. VERIFICAR SE NÃO EXPIROU
      if (preReg.expires_at && new Date(preReg.expires_at) < new Date()) {
        // Reverter a reserva se expirado
        await supabase
          .from("pre_registrations")
          .update({ is_used: false, used_at: null })
          .eq("id", preReg.id);

        logger.warn("auth", "Registro negado - autorização expirada", {
          email: formData.email.toLowerCase(),
        });
        toast({
          title: "Autorização Expirada",
          description:
            "Sua autorização de acesso expirou. Entre em contato com o suporte para renovar.",
          variant: "destructive",
          className: "bg-red-950/80 text-red-200 border-red-900",
        });
        return;
      }

      // 3. CRIAR CONTA NO SUPABASE AUTH
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email.toLowerCase(),
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: preReg.full_name,
            company: preReg.company || "",
          },
        },
      });
      if (authError) {
        logger.error("auth", "Falha no registro", {
          email: formData.email.toLowerCase(),
          error: authError.message,
        });
        // Reverter reserva do pre-cadastro se criacao de conta falhou
        await supabase
          .from("pre_registrations")
          .update({ is_used: false, used_at: null })
          .eq("id", preReg.id);

        if (authError.message.includes("already registered")) {
          toast({
            title: "Email Ja Cadastrado",
            description:
              "Este email ja possui uma conta ativa. Faca login ou recupere sua senha.",
            variant: "destructive",
            className: "bg-red-950/80 text-red-200 border-red-900",
          });
        } else {
          toast({
            title: "Erro no Cadastro",
            description: `Não foi possível criar a conta: ${authError.message}`,
            variant: "destructive",
            className: "bg-red-950/80 text-red-200 border-red-900",
          });
        }
        return;
      }
      if (authData.user) {
        // 4. FAZER LOGIN AUTOMATICO
        const { error: loginError } = await supabase.auth.signInWithPassword({
          email: formData.email.toLowerCase(),
          password: formData.password,
        });
        if (loginError) {
          toast({
            title: "Conta Criada!",
            description:
              "Conta criada com sucesso! Faça login para acessar a plataforma.",
            className: "bg-green-950/80 text-green-200 border-green-900",
          });
          navigate("/login");
          return;
        }

        // 6. VERIFICAR SE É ADMIN E REDIRECIONAR
        const { data: profile } = await supabase
          .from("profiles")
          .select("is_admin, full_name")
          .eq("id", authData.user.id)
          .maybeSingle();
        const isAdmin = profile?.is_admin || false;
        logger.info("auth", "Registro bem-sucedido", {
          userId: authData.user.id,
          email: formData.email.toLowerCase(),
        });
        toast({
          title: "Bem-vindo!",
          description: "Sua conta foi criada com sucesso.",
          className: "bg-green-950/80 text-green-200 border-green-900",
        });

        // Redirecionar baseado no tipo de usuário
        setTimeout(() => {
          if (isAdmin) {
            navigate("/admin");
          } else {
            void irParaOsAgentes("conta");
          }
        }, 1500);
      }
    } catch (error) {
      logger.error("auth", "Erro inesperado no registro", {
        error: error instanceof Error ? error.message : String(error),
      });
      toast({
        title: "Erro Inesperado",
        description:
          "Ocorreu um erro no sistema. Tente novamente em alguns minutos.",
        variant: "destructive",
        className: "bg-red-950/80 text-red-200 border-red-900",
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="space-y-6">
      <form onSubmit={handleRegister} className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="text-neutral-500 font-semibold text-xs uppercase tracking-[0.06em] pl-1"
          >
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            required
            readOnly={!!prefilledEmail}
            className={`h-12 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10 ${prefilledEmail ? "bg-neutral-100 cursor-not-allowed" : "bg-white/60"}`}
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="password"
            className="text-neutral-500 font-semibold text-xs uppercase tracking-[0.06em] pl-1"
          >
            Senha
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={formData.password}
            onChange={(e) => handleChange("password", e.target.value)}
            required
            minLength={8}
            className="h-12 bg-white/60 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10"
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="confirmPassword"
            className="text-neutral-500 font-semibold text-xs uppercase tracking-[0.06em] pl-1"
          >
            Confirmar Senha
          </Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            value={formData.confirmPassword}
            onChange={(e) => handleChange("confirmPassword", e.target.value)}
            required
            minLength={8}
            className="h-12 bg-white/60 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10"
          />
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="terms"
            checked={acceptTerms}
            onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
            className="mt-1 border-black/[0.15] data-[state=checked]:bg-accent-800 data-[state=checked]:text-white"
          />
          <Label
            htmlFor="terms"
            className="text-sm text-neutral-500 leading-relaxed cursor-pointer"
          >
            Aceito os{" "}
            <a
              href="/terms"
              className="text-accent-800 hover:text-accent-700 underline-offset-4 hover:underline transition-colors"
            >
              termos de uso
            </a>{" "}
            e{" "}
            <a
              href="/privacy"
              className="text-accent-800 hover:text-accent-700 underline-offset-4 hover:underline transition-colors"
            >
              política de privacidade
            </a>
          </Label>
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            disabled={isLoading || !acceptTerms}
            className="w-full h-12 bg-gradient-to-r from-accent-800 to-accent-600 hover:opacity-90 text-white font-semibold rounded-xl text-base shadow-[0_4px_16px_rgba(195,150,74,0.3)] hover:shadow-[0_6px_20px_rgba(195,150,74,0.4)] hover:-translate-y-0.5 transition-all duration-300"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Criando conta...
              </>
            ) : (
              <>
                <Crown className="w-5 h-5 mr-2" />
                Criar Conta Premium
              </>
            )}
          </Button>
        </div>
      </form>

      <div className="text-center">
        <p className="text-sm text-neutral-500">
          Já tem uma conta?{" "}
          <a
            href="/login"
            className="text-accent-800 hover:text-accent-700 font-medium transition-colors"
          >
            Fazer login
          </a>
        </p>
      </div>
    </div>
  );
}
